import axios from "http/customAxios";
import { Reservation } from "../../modification/model";
import { AxiosError } from "axios";

export interface PaymentOrderDto {
  hotelTicker: string;
  reservationId: string;
  paymentTypeTicker?: string;
  country: string;
  language: string;
  uuid?: string;
  status: string;
  amount: number;
  currency: string;
  additional?: string;
  service?: string;
  paymentProvider: string;
  customerId: string;
  customerEmail: string;
  token?: string;
  paid: boolean;
  safe: boolean;
  refunded: number;
  ipAddress?: string;
  cardNumber?: string;
  cardUserToken?: string;
}

export interface PaymentTransactionDto {
  hotelTicker: string;
  paymentProvider: string;
  id?: string;
  reservationId: string;
  description?: string;
  uuid?: string;
  operativeType?: string;
  operationType?: string;
  operationTypeDescription?: string;
  amount: number;
  authorization?: number | string;
  status: string;
  service?: string;
  user: string;
  orderId: string;
  paymentMethodSelected?: string;
  paymentMessage?: string;
  cardUserId?: string;
  cardUserToken?: string;
  subscriptionPlan?: string;
  cardHolderName?: string;
  cardNumber?: string;
  cardType?: string;
  createdAt: string;
  updatedAt: string;
  refundTransactionId?: string;
  normalizedOperationType: string;
  normalizedStatus: string;
}

export interface PaymentsApiAddonPaymentsChargePayloadDto extends PaymentsApiChargePayloadDto {
  itemAmounts: Record<string, number>;
}

export interface PaymentsApiChargePayloadDto {
  reservationId: string;
  amount: number;
  description: string;
  email: string;
}

export interface PaymentsApiAdyenChargePayloadDto extends PaymentsApiChargePayloadDto {
  itemAmounts: Record<string, number>;
}

export interface PaymentsApiPaycometChargePayloadDto extends PaymentsApiChargePayloadDto {
  itemAmounts: Record<string, number>;
}

export interface PaymentsApiPayNoPainV3ChargePayloadDto extends PaymentsApiChargePayloadDto {
  itemAmounts: Record<string, number>;
}

export interface PaymentsApiFreedomPayChargePayloadDto {
  reservation: Reservation;
  amount: number;
  description: string;
  email: string;
  itemAmounts: Record<string, number>;
}

export interface PaymentsApiSepteoPaymentsChargePayloadDto extends PaymentsApiChargePayloadDto {
  locale: string;
  currency: string;
  itemAmounts: Record<string, number>;
}

export interface PaymentsApiSipayChargePayloadDto extends PaymentsApiChargePayloadDto {
  itemAmounts: Record<string, number>;
}

export interface PaymentsApiStripeChargePayloadDto extends PaymentsApiChargePayloadDto {
  itemAmounts: Record<string, number>;
}

export interface PaymentsApiRedsysChargePayloadDto extends PaymentsApiChargePayloadDto {
  itemAmounts: Record<string, number>;
}

export interface PaymentsApiStripeRefundPayloadDto extends PaymentsApiRefundPayloadDto {
  itemAmounts: Record<string, number>;
}

export interface PaymentsApiRefundPayloadDto {
  reservationId: string;
  amount: number;
  description: string;
  email: string;
  transactionId: string;
}

export interface PaymentsApiAdyenRefundPayloadDto extends PaymentsApiRefundPayloadDto {
  itemAmounts: Record<string, number>;
}

export interface PaymentsApiPaycometRefundPayloadDto extends PaymentsApiRefundPayloadDto {
  itemAmounts: Record<string, number>;
}

export interface PaymentsApiPayNoPainV3RefundPayloadDto extends PaymentsApiRefundPayloadDto {
  itemAmounts: Record<string, number>;
}

export interface PaymentsApiSepteoPaymentsRefundPayloadDto
  extends Omit<PaymentsApiRefundPayloadDto, "transactionId"> {
  currency: string;
  locale: string;
  itemAmounts: Record<string, number>;
}

export interface PaymentsApiSipayRefundPayloadDto extends PaymentsApiRefundPayloadDto {
  itemAmounts: Record<string, number>;
  hotelTicker: string;
}

export interface PaymentsApiRedsysRefundPayloadDto extends PaymentsApiRefundPayloadDto {
  itemAmounts: Record<string, number>;
}

export class PaymentsApi {
  public readonly baseUrl: string;
  public readonly propertyTicker: string;

  constructor(baseUrl: string, propertyTicker: string) {
    this.baseUrl = baseUrl;
    this.propertyTicker = propertyTicker;
  }

  async addonPaymentsCharge(
    payload: PaymentsApiAddonPaymentsChargePayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(
      `${this.baseUrl}/addon-payments/${this.propertyTicker}/charges`,
      {
        ...payload,
        hotelTicker: this.propertyTicker,
      },
      {
        headers: {
          Authorization: authToken,
          "x-payment-method": paymentMethod,
        },
      }
    );
  }

  async paycometCharge(
    payload: PaymentsApiPaycometChargePayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(
      `${this.baseUrl}/paycomet/${this.propertyTicker}/charges`,
      {
        ...payload,
        hotelTicker: this.propertyTicker,
      },
      {
        headers: {
          Authorization: authToken,
          "x-payment-method": paymentMethod,
        },
      }
    );
  }

  async paycometRefund(
    payload: PaymentsApiPaycometRefundPayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(
      `${this.baseUrl}/paycomet/${this.propertyTicker}/refund`,
      {
        ...payload,
        hotelTicker: this.propertyTicker,
      },
      {
        headers: {
          Authorization: authToken,
          "x-payment-method": paymentMethod,
        },
      }
    );
  }

  async getPaymentOrder(
    reservationId: string | number,
    authToken: string
  ): Promise<PaymentOrderDto> {
    const endpoint = `${this.baseUrl}/payment-orders/${this.propertyTicker}/reservations/${reservationId}`;
    const response = await axios.get(endpoint, {
      headers: {
        Authorization: authToken,
      },
    });
    return response.data;
  }

  async listTransactions(
    reservationId: string | number,
    authToken: string
  ): Promise<{ pageSize: number; data: PaymentTransactionDto[] }> {
    const endpoint = `${this.baseUrl}/transactions/${this.propertyTicker}/reservations/${reservationId}`;
    const response = await axios.get(endpoint, {
      headers: {
        Authorization: authToken,
      },
    });
    return response.data;
  }

  async listStoreOrderTransactions(
    orderCode: string | number,
    authToken: string
  ): Promise<{ pageSize: number; data: PaymentTransactionDto[] }> {
    const endpoint = `${this.baseUrl}/transactions/${this.propertyTicker}/orders/${orderCode}`;
    const response = await axios.get(endpoint, {
      headers: {
        Authorization: authToken,
      },
    });
    return response.data;
  }

  async adyenCharge(
    payload: PaymentsApiAdyenChargePayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(`${this.baseUrl}/adyen/${this.propertyTicker}/charges`, payload, {
      headers: {
        Authorization: authToken,
        "x-payment-method": paymentMethod,
      },
    });
  }

  async stripeCharge(
    payload: PaymentsApiStripeChargePayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(`${this.baseUrl}/stripe/${this.propertyTicker}/charges`, payload, {
      headers: {
        Authorization: authToken,
        "x-payment-method": paymentMethod,
      },
    });
  }

  async payNoPainV3Charge(
    payload: PaymentsApiPayNoPainV3ChargePayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(
      `${this.baseUrl}/paynopain/v3/${this.propertyTicker}/charges`,
      {
        ...payload,
        hotelTicker: this.propertyTicker,
      },
      {
        headers: {
          Authorization: authToken,
          "x-payment-method": paymentMethod,
        },
      }
    );
  }

  async payNoPainV3Refund(
    payload: PaymentsApiPayNoPainV3RefundPayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(
      `${this.baseUrl}/paynopain/v3/${this.propertyTicker}/refund`,
      {
        ...payload,
        hotelTicker: this.propertyTicker,
      },
      {
        headers: {
          Authorization: authToken,
          "x-payment-method": paymentMethod,
        },
      }
    );
  }

  async payNoPainSdkRefund(
    payload: PaymentsApiRefundPayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(
      `${this.baseUrl}/paynopain/sdk/${this.propertyTicker}/refund`,
      {
        ...payload,
        hotelTicker: this.propertyTicker,
      },
      {
        headers: {
          Authorization: authToken,
          "x-payment-method": paymentMethod,
        },
      }
    );
  }

  async stripeRefund(
    payload: PaymentsApiStripeRefundPayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(
      `${this.baseUrl}/stripe/${this.propertyTicker}/refund`,
      {
        ...payload,
        hotelTicker: this.propertyTicker,
      },
      {
        headers: {
          Authorization: authToken,
          "x-payment-method": paymentMethod,
        },
      }
    );
  }

  async adyenRefund(
    payload: PaymentsApiAdyenRefundPayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(
      `${this.baseUrl}/adyen/${this.propertyTicker}/refund`,
      {
        ...payload,
        hotelTicker: this.propertyTicker,
      },
      {
        headers: {
          Authorization: authToken,
          "x-payment-method": paymentMethod,
        },
      }
    );
  }

  async freedomCharge(
    { amount, email, reservation, description, itemAmounts }: PaymentsApiFreedomPayChargePayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    try {
      await axios.post(
        `${this.baseUrl}/freedompay/${this.propertyTicker}/charges`,
        {
          amount,
          description,
          email,
          itemAmounts,
          reservation: {
            hotelTicker: this.propertyTicker,
            reservationId: reservation.id,
            roomStays: reservation.roomReservations.map(it => ({
              startDate: it.checkIn,
              endDate: it.checkOut,
              totalRate: it.totalAmount,
            })),
          },
        },
        {
          headers: {
            Authorization: authToken,
            "x-payment-method": paymentMethod,
          },
        }
      );
    } catch (error) {
      // AWS Gateway timeout. FreedomPay create charge timeout is 35 seconds, but AWS Gateway timeout
      // is 29 seconds, so, we need to handle this case to not show an error to the user if the charge
      // is finally created correctly
      if (error instanceof AxiosError && error.response && error.response.status === 504) {
        console.warn("AWS Gateway Timeout. Waiting for FreedomPay charge creation timeout...");

        // we will wait another 6-7s to retrieve the transactions and view if a transaction is saved
        await new Promise(resolve => setTimeout(resolve, 7000));
        const transactions = await this.listTransactions(reservation.id, authToken);

        const currentTransaction = transactions.data.find(
          t => t.amount === amount && t.description === description
        );
        if (!currentTransaction || currentTransaction.status !== "SUCCESS") {
          throw new Error("FreedomPay charge creation failed after AWS Gateway timeout");
        }
      }
    }
  }

  async generateSepteoCardShowLink(
    propertyTicker: string,
    reservationId: string,
    authToken: string
  ) {
    const response = await axios.post<{ url: string }>(
      `${this.baseUrl}/septeo/vault/${propertyTicker}/cards/${reservationId}/show`,
      undefined,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );
    const { url } = response.data;
    return url;
  }

  async septeoPaymentsCharge(
    payload: PaymentsApiSepteoPaymentsChargePayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(`${this.baseUrl}/septeo/payments/${this.propertyTicker}/charges`, payload, {
      headers: {
        Authorization: authToken,
        "x-payment-method": paymentMethod,
      },
    });
  }

  async septeoPaymentsRefund(
    payload: PaymentsApiSepteoPaymentsRefundPayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(
      `${this.baseUrl}/septeo/payments/${this.propertyTicker}/refund`,
      {
        ...payload,
      },
      {
        headers: {
          Authorization: authToken,
          "x-payment-method": paymentMethod,
        },
      }
    );
  }

  async sipayCharge(
    payload: PaymentsApiSipayChargePayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(`${this.baseUrl}/sipay/${this.propertyTicker}/charges`, payload, {
      headers: {
        Authorization: authToken,
        "x-payment-method": paymentMethod,
      },
    });
  }

  async sipayRefund(
    payload: PaymentsApiSipayRefundPayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(`${this.baseUrl}/sipay/${this.propertyTicker}/refund`, payload, {
      headers: {
        Authorization: authToken,
        "x-payment-method": paymentMethod,
      },
    });
  }

  async redsysRefund(
    payload: PaymentsApiRedsysRefundPayloadDto,
    authToken: any,
    paymentMethod: string
  ) {
    await axios.post(
      `${this.baseUrl}/tpv/${this.propertyTicker}/refund`,
      {
        ...payload,
        hotelTicker: this.propertyTicker,
      },
      {
        headers: {
          Authorization: authToken,
          "x-payment-method": paymentMethod,
        },
      }
    );
  }

  async redsysCharge(
    payload: PaymentsApiRedsysChargePayloadDto,
    authToken: string,
    paymentMethod: string
  ): Promise<void> {
    await axios.post(`${this.baseUrl}/tpv/${this.propertyTicker}/charges`, payload, {
      headers: {
        Authorization: authToken,
        "x-payment-method": paymentMethod,
      },
    });
  }
}
